import { useRef } from "react";
import { Avatar, Flex, Grid, Text, useDisclosure } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "react-query";
import { CheckCircleIcon, AddIcon } from "@chakra-ui/icons";
import { Plus } from "lucide-react";
import {
	type User,
	UserCenterAccess,
	UserCenterRole,
} from "../../../types/user.types";
import { useNewOrderStore } from "../../../stores/order.store";
import Tooltip from "../../../components/Tooltip";
import SimpleModal from "../../../components/modals/SimpleModal";
import InviteUserForm, {
	type InviteUserFormHandler,
} from "../../../components/forms/LinkUserForm";
import { sendInvitations } from "../../../mutations/center.mutations";
import AudioForm, {
	type AudioFormHandler,
} from "../../../components/forms/AudioForm";
import { createUserAuthenticated } from "../../../mutations/user.mutations";
import i18n from "../../../i18n";
import { useConnectedUser } from "../../../hooks/user.hooks";
import { useSimpleToast } from "../../../hooks/toast.hooks";

/**
 * Display audio badge.
 */
const SelectUserCircleBadge = ({
	value,
	role = false,
	userSelected,
	setUserSelected,
}: {
	value: Partial<User>;
	role?: boolean;
	userSelected?: User;
	setUserSelected: (user?: User) => void;
}) => {
	return (
		<Flex
			position="relative"
			direction="column"
			alignItems="center"
			gap={4}
			onClick={() => {
				setUserSelected(value as User);
			}}
		>
			<Tooltip content={`${value.firstName} ${value.lastName}`} w={"10rem"}>
				<Avatar
					name={`${value.firstName} ${value.lastName}`}
					_hover={{ transform: "scale(1.05)" }}
					transition="all 100ms east-out"
					src="https://bit.ly/cbouchard"
					h="96px"
					w="96px"
					outline={
						userSelected?.id === value.id ? "solid 2px #1B58F5" : undefined
					}
				/>
			</Tooltip>
			{userSelected?.id === value.id && (
				<CheckCircleIcon
					color="brand.600"
					position="absolute"
					right="4px"
					top="6px"
					bgColor="white"
					borderRadius="xl"
				/>
			)}
			<Text
				textTransform="capitalize"
				lineHeight="21px"
				fontSize="14px"
				fontWeight="500"
				color="gray.700"
			>
				{value.firstName}
			</Text>
			{role && (
				<Text
					textTransform="capitalize"
					lineHeight="18px"
					fontSize="12px"
					fontWeight="400"
					color="gray.500"
				>
					{i18n.t(value.role ?? "")}
				</Text>
			)}
		</Flex>
	);
};

type AddUserCircleBadgeProps = {
	centerId?: string | number;
};

/**
 * Display add audio badge
 */
const AddUserCircleBadge = ({ centerId }: AddUserCircleBadgeProps) => {
	const user = useConnectedUser();
	const createAudioModal = useDisclosure();
	const audioFormRef = useRef<AudioFormHandler | null>(null);
	const queryClient = useQueryClient();
	const toast = useSimpleToast();
	const createUserMutation = useMutation(createUserAuthenticated, {
		onSuccess: () => {
			queryClient.invalidateQueries("center-audios");
			queryClient.invalidateQueries("center-users-paginated");
			createAudioModal.onClose();
		},
		onError: () => {
			toast.error("Erreur", "Erreur lors de la création de l'utilisateur");
		},
	});

	if (!user || !centerId) return null;
	if (user.access !== UserCenterAccess.ADMIN && user.isSuperAdmin === false)
		return null;
	return (
		<Flex
			position="relative"
			direction="column"
			alignItems="center"
			justifyContent="center"
			gap={4}
			w="96px"
			h="96px"
			rounded="full"
			className="border-2! border-brand-600! border-dashed bg-brand-50"
			onClick={createAudioModal.onOpen}
			_hover={{
				cursor: "pointer",
				transform: "scale(1.1)",
				transition: "transform 0.2s ease",
			}}
		>
			<Plus className="text-brand-600 size-10" />

			<SimpleModal
				title="Ajouter un audioprothésiste"
				isOpen={createAudioModal.isOpen}
				onClose={createAudioModal.onClose}
				onClick={async () => {
					if (!audioFormRef.current) return;
					if (createUserMutation.isLoading) return;

					const valid = await audioFormRef.current.valid();
					if (valid === false) return;

					const values = audioFormRef.current.values();
					createUserMutation.mutate({
						invitation: true,
						activated: true,
						user: {
							email: values.email,
							firstName: values.firstName,
							lastName: values.lastName,
						},
						centerId: centerId
							? typeof centerId === "string"
								? Number.parseInt(centerId, 10)
								: centerId
							: undefined,
					});
				}}
				isPending={createUserMutation.isLoading}
			>
				<AudioForm ref={audioFormRef} />
			</SimpleModal>
		</Flex>
	);
};

/**
 * New audio invitation modal.
 */
const OrdersNewPageChooseAudioNewAudioModal = ({
	isOpen,
	onClose,
}: {
	isOpen: boolean;
	onClose: () => void;
}) => {
	const { getCenter } = useNewOrderStore();
	const newAudioFormRef = useRef<InviteUserFormHandler>(null);
	const { mutate: mutateSendInvitations } = useMutation(sendInvitations);
	const handleNewAudio = () => {
		const center = getCenter();
		if (newAudioFormRef.current && center) {
			mutateSendInvitations({
				center,
				users: newAudioFormRef.current.values(),
			});
			onClose();
		}
	};

	return (
		<SimpleModal
			icon={<AddIcon color="brand.700" />}
			title="Ajouter un audioprothésiste"
			isOpen={isOpen}
			onClose={onClose}
			w="min(60rem, 90vw)"
			onClick={handleNewAudio}
		>
			<InviteUserForm
				ref={newAudioFormRef}
				role={UserCenterRole.AUDIOPROSTHESIST}
			></InviteUserForm>
		</SimpleModal>
	);
};

export const SelectUserCircle = ({
	users,
	w = "100%",
	role = false,
	userSelected,
	setUserSelected,
	centerId,
}: {
	users: Partial<User>[];
	w?: string;
	role?: boolean;
	userSelected?: User;
	setUserSelected: (user?: User) => void;
	centerId?: string | number;
}) => {
	const newAudioModal = useDisclosure();

	return (
		<Grid
			templateColumns="1fr 1fr 1fr 1fr 1fr"
			w={w}
			justifyItems="center"
			gap="20px 4px"
			paddingY="24px"
		>
			{users?.map((user) => (
				<SelectUserCircleBadge
					key={user.id}
					value={user}
					role={role}
					userSelected={userSelected}
					setUserSelected={setUserSelected}
				/>
			))}

			<AddUserCircleBadge centerId={centerId} />

			<OrdersNewPageChooseAudioNewAudioModal
				isOpen={newAudioModal.isOpen}
				onClose={newAudioModal.onClose}
			/>
		</Grid>
	);
};
